import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import TranslateWidget from "@/components/TranslateWidget";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Calendar, Clock, MapPin, Users, ArrowLeft, Download, Loader2, CheckCircle2, ChevronDown,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_crm-news-hub/artifacts/srv2lps9_ChatGPT%20Image%20Apr%202%2C%202026%2C%2010_47_10%20AM.png";

const TryoutsPage = () => {
  const [tryouts, setTryouts] = useState([]);
  const [logoUrl, setLogoUrl] = useState(LOGO_URL);
  const [cmsContent, setCmsContent] = useState({});
  const [expandedTryout, setExpandedTryout] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingForm, setBookingForm] = useState({ player_name: "", parent_name: "", email: "", phone: "", player_age: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tryRes, contentRes] = await Promise.all([
          axios.get(`${API}/tryouts/public`),
          axios.get(`${API}/site-content/public`).catch(() => null),
        ]);
        setTryouts(tryRes.data);
        if (contentRes?.data?.logo_url) setLogoUrl(contentRes.data.logo_url);
        setCmsContent(contentRes?.data?.tryouts_page || {});
      } catch (e) {
        console.error("Failed to fetch tryouts:", e);
      }
    };
    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr + "T00:00:00");
      return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    } catch { return dateStr; }
  };

  const handleBook = async (tryoutId) => {
    if (!bookingForm.player_name.trim() || !bookingForm.parent_name.trim() || !bookingForm.email.trim()) {
      toast.error("Player name, parent name, and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/tryouts/${tryoutId}/book`, {
        ...bookingForm,
        slot_id: selectedSlot || "",
      });
      setBooked(tryoutId);
      toast.success(res.data.message || "Registration confirmed!");
      // Refresh tryout data for updated slot counts
      const tryRes = await axios.get(`${API}/tryouts/public`);
      setTryouts(tryRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExpand = (tryoutId) => {
    if (expandedTryout === tryoutId) {
      setExpandedTryout(null);
      setSelectedSlot(null);
    } else {
      setExpandedTryout(tryoutId);
      setSelectedSlot(null);
      setBooked(null);
      setBookingForm({ player_name: "", parent_name: "", email: "", phone: "", player_age: "", message: "" });
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F3]" data-testid="tryouts-page">
      <Helmet>
        <title>Tryout Schedule | Commonwealth FC Youth Soccer Charlottesville</title>
        <meta name="description" content="Commonwealth FC tryout schedule for Charlottesville and Albemarle County. Find upcoming tryout dates, locations, age groups U8-U19, and register today." />
        <meta property="og:title" content="Tryouts | Commonwealth FC" />
        <meta property="og:description" content="Find upcoming tryout dates, locations, and age groups for Commonwealth FC youth soccer." />
        <meta property="og:url" content="https://commonwealthfc.org/tryouts" />
        <link rel="canonical" href="https://commonwealthfc.org/tryouts" />
      </Helmet>

      <nav className="bg-[#0C2E3C] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            <img src={logoUrl} alt="Commonwealth FC" className="h-9 w-auto" />
            <span className="text-sm font-bold uppercase tracking-wider text-[#F0F4F3] hidden sm:block">Commonwealth FC</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-[#F0F4F3]/80 hover:text-[#C8E64E] text-sm"><ArrowLeft size={16} className="inline mr-1" /> Back</Link>
            <TranslateWidget />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#0C2E3C] mb-4" data-testid="tryouts-heading">
            {cmsContent.title || "TRYOUT SCHEDULE"}
          </h1>
          <p className="text-[#6B8A8F] text-base">
            {cmsContent.subtitle || "Find an upcoming tryout, pick your slot, and register in seconds."}
          </p>
        </div>

        {tryouts.length === 0 ? (
          <div className="text-center py-16">
            <Calendar size={48} className="mx-auto text-[#6B8A8F]/30 mb-4" />
            <p className="text-[#6B8A8F]">{cmsContent.empty_message || "No upcoming tryouts scheduled. Check back soon!"}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {tryouts.map((tryout) => {
              const isExpanded = expandedTryout === tryout.tryout_id;
              const isBooked = booked === tryout.tryout_id;
              const hasSlots = tryout.self_schedule && tryout.slots?.length > 0;

              return (
                <div key={tryout.tryout_id} className="bg-white border border-[#0C2E3C]/10 hover:border-[#C8E64E]/50 transition-colors" data-testid={`tryout-card-${tryout.tryout_id}`}>
                  {/* Tryout Header */}
                  <div className="p-6 sm:p-8">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1">
                        <h2 className="font-bold text-xl text-[#0C2E3C] mb-3">{tryout.title}</h2>
                        <div className="space-y-2 text-sm text-[#6B8A8F]">
                          <p className="flex items-center gap-2"><Calendar size={16} className="text-[#C8E64E]" /> {formatDate(tryout.date)}</p>
                          <p className="flex items-center gap-2"><Clock size={16} className="text-[#C8E64E]" /> {tryout.time}</p>
                          <p className="flex items-center gap-2"><MapPin size={16} className="text-[#C8E64E]" /> {tryout.location}</p>
                          {tryout.age_groups?.length > 0 && (
                            <p className="flex items-center gap-2"><Users size={16} className="text-[#C8E64E]" /> {tryout.age_groups.join(", ")}</p>
                          )}
                        </div>
                        {tryout.description && <p className="mt-4 text-[#6B8A8F] text-sm leading-relaxed">{tryout.description}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {tryout.max_spots && (
                          <p className="text-xs text-[#6B8A8F]">{Math.max(0, tryout.max_spots - (tryout.assigned_count || 0))} spots remaining</p>
                        )}
                        <div className="flex gap-2">
                          <a href={`${API}/tryouts/${tryout.tryout_id}/calendar.ics`} download className="inline-flex items-center gap-1 text-xs text-[#0C2E3C]/60 hover:text-[#0C2E3C] transition-colors border border-[#0C2E3C]/20 px-3 py-2 rounded-none" data-testid={`ics-download-${tryout.tryout_id}`}>
                            <Download size={14} /> Add to Calendar
                          </a>
                          {tryout.self_schedule ? (
                            <Button onClick={() => toggleExpand(tryout.tryout_id)} className="bg-[#0C2E3C] hover:bg-[#0C2E3C]/90 text-[#C8E64E] font-bold uppercase tracking-wider text-sm px-6 h-10 rounded-none" data-testid={`tryout-schedule-${tryout.tryout_id}`}>
                              {isExpanded ? "Close" : "Pick a Slot"} <ChevronDown size={16} className={`ml-1 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                            </Button>
                          ) : (
                            <Link to="/join">
                              <Button className="bg-[#0C2E3C] hover:bg-[#0C2E3C]/90 text-[#F0F4F3] font-bold uppercase tracking-wider text-sm px-6 h-10 rounded-none" data-testid={`tryout-register-${tryout.tryout_id}`}>
                                Register Interest
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Self-Schedule Panel */}
                  {isExpanded && tryout.self_schedule && (
                    <div className="border-t border-[#0C2E3C]/10 bg-[#F0F4F3]/50" data-testid={`schedule-panel-${tryout.tryout_id}`}>
                      {isBooked ? (
                        <div className="p-8 text-center" data-testid="booking-success">
                          <CheckCircle2 size={48} className="mx-auto text-green-500 mb-3" />
                          <h3 className="font-bold text-xl text-[#0C2E3C] mb-2">You{"'"}re Registered!</h3>
                          <p className="text-[#6B8A8F] text-sm">A confirmation email has been sent. We look forward to seeing you!</p>
                          <a href={`${API}/tryouts/${tryout.tryout_id}/calendar.ics${selectedSlot ? `?slot_id=${selectedSlot}` : ""}`} download className="inline-flex items-center gap-1 mt-4 text-sm text-[#0C2E3C] font-medium border border-[#0C2E3C]/20 px-4 py-2">
                            <Download size={14} /> Download Calendar Invite
                          </a>
                        </div>
                      ) : (
                        <div className="p-6 sm:p-8">
                          {/* Slot Selection */}
                          {hasSlots && (
                            <div className="mb-6">
                              <h3 className="text-sm font-bold uppercase text-[#0C2E3C]/60 tracking-wider mb-3">Select a Time Slot</h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {tryout.slots.map((slot) => {
                                  const isFull = slot.max_spots && (slot.booked_count || 0) >= slot.max_spots;
                                  const isSelected = selectedSlot === slot.slot_id;
                                  return (
                                    <button key={slot.slot_id} disabled={isFull} onClick={() => setSelectedSlot(isSelected ? null : slot.slot_id)}
                                      className={`flex items-center justify-between p-3 border text-left transition-all ${
                                        isFull ? "opacity-50 cursor-not-allowed bg-gray-100 border-gray-200"
                                        : isSelected ? "border-[#C8E64E] bg-[#C8E64E]/10 ring-2 ring-[#C8E64E]"
                                        : "border-[#0C2E3C]/15 bg-white hover:border-[#C8E64E]"
                                      }`}
                                      data-testid={`slot-${slot.slot_id}`}
                                    >
                                      <div>
                                        <span className="font-bold text-sm text-[#0C2E3C]">{slot.time}{slot.end_time ? ` - ${slot.end_time}` : ""}</span>
                                        {slot.age_group && <span className="ml-2 text-xs text-[#6B8A8F]">({slot.age_group})</span>}
                                      </div>
                                      <div className="text-right">
                                        {isFull ? (
                                          <span className="text-xs font-bold text-red-500">FULL</span>
                                        ) : slot.max_spots ? (
                                          <span className="text-xs text-[#6B8A8F]">{slot.max_spots - (slot.booked_count || 0)} spots</span>
                                        ) : null}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Booking Form */}
                          <div>
                            <h3 className="text-sm font-bold uppercase text-[#0C2E3C]/60 tracking-wider mb-3">Your Details</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-medium text-[#6B8A8F]">Player Name *</label>
                                <Input value={bookingForm.player_name} onChange={(e) => setBookingForm((p) => ({ ...p, player_name: e.target.value }))} placeholder="Player's full name" className="mt-1 rounded-none border-[#0C2E3C]/20" data-testid="book-player-name" />
                              </div>
                              <div>
                                <label className="text-xs font-medium text-[#6B8A8F]">Parent/Guardian Name *</label>
                                <Input value={bookingForm.parent_name} onChange={(e) => setBookingForm((p) => ({ ...p, parent_name: e.target.value }))} placeholder="Parent or guardian" className="mt-1 rounded-none border-[#0C2E3C]/20" data-testid="book-parent-name" />
                              </div>
                              <div>
                                <label className="text-xs font-medium text-[#6B8A8F]">Email *</label>
                                <Input type="email" value={bookingForm.email} onChange={(e) => setBookingForm((p) => ({ ...p, email: e.target.value }))} placeholder="your@email.com" className="mt-1 rounded-none border-[#0C2E3C]/20" data-testid="book-email" />
                              </div>
                              <div>
                                <label className="text-xs font-medium text-[#6B8A8F]">Phone</label>
                                <Input value={bookingForm.phone} onChange={(e) => setBookingForm((p) => ({ ...p, phone: e.target.value }))} placeholder="(555) 555-5555" className="mt-1 rounded-none border-[#0C2E3C]/20" data-testid="book-phone" />
                              </div>
                              <div>
                                <label className="text-xs font-medium text-[#6B8A8F]">Player Age / Birth Year</label>
                                <Input value={bookingForm.player_age} onChange={(e) => setBookingForm((p) => ({ ...p, player_age: e.target.value }))} placeholder="e.g. 12 or 2014" className="mt-1 rounded-none border-[#0C2E3C]/20" data-testid="book-age" />
                              </div>
                            </div>
                            <div className="mt-3">
                              <label className="text-xs font-medium text-[#6B8A8F]">Anything we should know?</label>
                              <Textarea value={bookingForm.message} onChange={(e) => setBookingForm((p) => ({ ...p, message: e.target.value }))} placeholder="Previous experience, position preference, etc." className="mt-1 rounded-none border-[#0C2E3C]/20 min-h-[60px]" data-testid="book-message" />
                            </div>
                            <Button onClick={() => handleBook(tryout.tryout_id)} disabled={submitting} className="mt-4 w-full sm:w-auto bg-[#C8E64E] hover:bg-[#b8d43e] text-[#0C2E3C] font-bold uppercase tracking-wider text-sm px-8 h-12 rounded-none" data-testid="submit-booking-btn">
                              {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <CheckCircle2 size={16} className="mr-2" />}
                              Confirm Registration
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <footer className="bg-[#0C2E3C] py-6 text-center">
        <p className="text-[#F0F4F3]/40 text-sm">&copy; {new Date().getFullYear()} Commonwealth FC. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default TryoutsPage;
